import { BRAND_KEYS, CHART_STRATEGIES, DEFAULT_BRAND } from "@outpave/ds";
import type { BrandIdentity, ChartStrategy } from "@outpave/ds";
import { ThemeColors } from "../utils/themeContextHelper";

/** The package's stock chart slots are this strategy's output for its own brand. */
export const DEFAULT_CHART_STRATEGY: ChartStrategy = "analogous";

/** The stored 3rd chart pick: "1" to "6" is a generated companion, anything else the tertiary. */
export const chartThirdIndex = (value?: string): number =>
  value && /^[0-6]$/.test(value) ? Number(value) : 0;

/**
 * The /custom-branding record onto the DS identity knobs (D1). The DS ignores a
 * partial identity (needs primary+secondary+tertiary together), so this
 * resolver completes partial records from base seeds.
 * A bright navBackground makes odd grey ramps: branding-record data problem.
 */
export function resolveBrandIdentity(
  companyColors: Partial<ThemeColors> | null,
  platformColors: Partial<ThemeColors> | null | undefined,
): BrandIdentity | undefined {
  // Per-knob layering, not whole-record: a company that customized only its
  // background must still get it applied over the platform's primary. Empty
  // string means "reset to default" in the legacy records, so falsy is
  // absent.
  const pick = (key: keyof ThemeColors): string | undefined =>
    companyColors?.[key] || platformColors?.[key] || undefined;

  const primary = pick("primary");
  const secondary = pick("secondary");
  const tertiary = pick("tertiary");
  const background = pick("navBackground");
  const success = pick("success");
  const warning = pick("warning");
  const danger = pick("danger");
  const info = pick("info");
  const strategy = pick("chartStrategy");
  const leadWithSecondary = pick("chartLeadWithSecondary") === "true";
  const third = chartThirdIndex(pick("chartThirdColor"));

  const base = BRAND_KEYS[DEFAULT_BRAND];
  const status: NonNullable<BrandIdentity["status"]> = {};
  if (success) status.success = success;
  if (warning) status.warning = warning;
  if (danger) status.danger = danger;
  if (info) status.info = info;

  // Only a strategy the package still ships. A retired name in an old record
  // would otherwise reach `buildScale` and throw.
  const chartStrategy = CHART_STRATEGIES.includes(strategy as ChartStrategy)
    ? (strategy as ChartStrategy)
    : undefined;

  const hasIdentity = primary || secondary || tertiary || background;
  if (
    !hasIdentity &&
    Object.keys(status).length === 0 &&
    !chartStrategy &&
    !leadWithSecondary &&
    !third
  ) {
    return undefined;
  }

  return {
    primary: primary ?? base.primary,
    secondary: secondary ?? base.secondary,
    tertiary: tertiary ?? base.tertiary,
    ...(background ? { background } : {}),
    ...(Object.keys(status).length > 0 ? { status } : {}),
    // Unset is Analogous, so picked colours drive slots 4 to 8 as well.
    chart: {
      strategy: chartStrategy ?? DEFAULT_CHART_STRATEGY,
      leadWithSecondary,
      ...(third ? { third } : {}),
    },
  };
}
